<template>
  <v-row no-gutters>
    <v-col cols="12">
      <v-card>
        <v-card-title class="pb-0">
          <h1 class="headline font-weight-medium mb-2 mx-1">
            {{ isNew ? "Створення пакету сплати" : "Пакет сплати" }}
          </h1>
          <v-spacer />
          <ActionsModalWindow
            v-if="modal"
            :id="id"
            component-name="PaymentPackage"
            @close="$emit('close')"
          />
        </v-card-title>
        <v-form>
          <v-card-text class="py-0">
            <v-row>
              <v-col sm="8" cols="12" class="pb-0">
                <CustomTextField
                  v-model="title"
                  label="Найменування*"
                  class="mx-1"
                  :error-messages="$v.title.$error ? errors.TITLE : []"
                  :hide-details="false"
                  @input="$v.title.$touch()"
                />
              </v-col>
              <v-col sm="4" cols="12" class="pb-0">
                <CustomTextField
                  v-model="article"
                  class="mx-1"
                  label="Артикул"
                  required
                  :error-messages="$v.article.$error ? errors.ARTICLE : []"
                  :hide-details="false"
                  @input="$v.article.$touch()"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" class="pt-0">
                <v-radio-group
                  v-model="paymentPackageType"
                  row
                  class="ml-1 mb-n7"
                  :error-messages="
                    $v.paymentPackageType.$error
                      ? errors.PAYMENT_PACKAGE_TYPE
                      : []
                  "
                  :hide-details="false"
                  @input="$v.paymentPackageType.$touch()"
                >
                  <v-radio
                    :value="paymentPackageTypes.SUPPORT.value"
                    :label="paymentPackageTypes.SUPPORT.title"
                    @change="isChanged = true"
                  />
                  <v-radio
                    :value="paymentPackageTypes.PROJECT.value"
                    :label="paymentPackageTypes.PROJECT.title"
                    @change="isChanged = true"
                  />
                </v-radio-group>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <h2 class="subtitle-1 font-weight-medium">
                  Варіанти відвантаження*:
                </h2>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-radio-group
                  v-model="useVAT"
                  row
                  class="ml-1 mt-n3 mb-n6"
                  @change="isChanged = true"
                >
                  <v-radio
                    :label="useVatTypes.WITH_VAT.title"
                    :value="useVatTypes.WITH_VAT.label"
                  />
                  <v-radio
                    :label="useVatTypes.WITHOUT_VAT.title"
                    :value="useVatTypes.WITHOUT_VAT.label"
                  />
                  <v-radio
                    :label="useVatTypes.BOTH_VAT.title"
                    :value="useVatTypes.BOTH_VAT.label"
                  />
                </v-radio-group>
              </v-col>
            </v-row>

            <v-row>
              <v-col md="4" sm="6" cols="12" class="pb-0">
                <DecimalEditor
                  v-model="amount"
                  :disabled="disableAmount"
                  :scale="2"
                  :label="amountWithoutLabel"
                  class="ma-1"
                  :error-messages="$v.amount.$error ? errors.AMOUNT : []"
                  :hide-details="false"
                  @input="$v.amount.$touch()"
                />
              </v-col>
              <v-col md="4" sm="6" cols="12" class="pb-0">
                <DecimalEditor
                  v-model="amountWithVAT"
                  :disabled="disableAmountWithVAT"
                  :scale="2"
                  :label="amountWithLabel"
                  class="ma-1"
                  :error-messages="
                    $v.amountWithVAT.$error ? errors.AMOUNT_WITH_VAT : []
                  "
                  :hide-details="false"
                  @input="$v.amountWithVAT.$touch()"
                />
              </v-col>
              <v-col md="4" sm="12" cols="12" class="pb-0">
                <DecimalEditor
                  v-model="baseRateHours"
                  :scale="0"
                  label="Годин за базовим тарифом"
                  class="ma-1"
                  @input="isChanged = true"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" class="mt-n5 pb-0">
                <v-textarea
                  v-model="comments"
                  label="Примітки"
                  class="ma-1"
                  dense
                  outlined
                  @input="isChanged = true"
                />
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-actions class="mt-n3">
            <PrimaryBtn
              :has-warnings="$v.$invalid"
              :loading="loading"
              @click="save"
            />
            <v-btn v-if="!modal" @click="$router.go(-1)">
              Відмінити
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import DecimalEditor from "../../../components/DecimalEditor.vue";
import * as packagesApi from "../../../backend/packagesApi.js";
import useVatTypes from "../../../helpers/useVatTypes.js";
import paymentPackageTypes from "../../../helpers/paymentPackageTypes.js";
import PrimaryBtn from "@/components/PrimaryBtn.vue";
import { ERRORS } from "@/helpers";
import { required } from "vuelidate/lib/validators";

export default {
  components: {
    DecimalEditor,
    PrimaryBtn,
    ActionsModalWindow() {
      return import("@/components/ActionsModalWindow.vue");
    },
  },
  props: ["id", "modal"],
  data: () => ({
    title: "",
    article: "",
    paymentPackageType: null,
    amount: 0,
    amountWithVAT: 0,
    comments: "",
    loading: false,
    isChanged: false,
    useVAT: null,
    baseRateHours: 0,
  }),

  validations: {
    title: {
      required,
    },
    article: {
      required,
    },
    paymentPackageType: {
      required,
    },
    useVAT: {
      required,
    },
    amount: {
      minValue(value) {
        return this.disableAmount || value > 0;
      },
    },
    amountWithVAT: {
      minValue(value) {
        return this.disableAmountWithVAT || value > 0;
      },
    },
  },

  computed: {
    isNew() {
      return +this.id ? false : true;
    },
    disableAmount() {
      if (this.useVAT === this.useVatTypes.WITH_VAT.label) {
        return true;
      } else if (this.useVAT === this.useVatTypes.WITHOUT_VAT.label) {
        return false;
      } else {
        return false;
      }
    },
    disableAmountWithVAT() {
      if (this.useVAT === this.useVatTypes.WITH_VAT.label) {
        return false;
      } else if (this.useVAT === this.useVatTypes.WITHOUT_VAT.label) {
        return true;
      } else {
        return false;
      }
    },
    amountWithLabel() {
      if (this.useVAT === this.useVatTypes.WITH_VAT.label) {
        return "Вартість, грн з ПДВ*";
      } else if (this.useVAT === this.useVatTypes.WITHOUT_VAT.label) {
        return "Вартість, грн з ПДВ";
      } else {
        return "Вартість, грн з ПДВ*";
      }
    },
    amountWithoutLabel() {
      if (this.useVAT === this.useVatTypes.WITH_VAT.label) {
        return "Вартість, грн без ПДВ";
      } else if (this.useVAT === this.useVatTypes.WITHOUT_VAT.label) {
        return "Вартість, грн без ПДВ*";
      } else {
        return "Вартість, грн без ПДВ*";
      }
    },
    useVatTypes() {
      return useVatTypes;
    },
    paymentPackageTypes() {
      return paymentPackageTypes;
    },
    errors() {
      return {
        TITLE: [ERRORS.REQUIRED],
        ARTICLE: [ERRORS.REQUIRED],
        PAYMENT_PACKAGE_TYPE: [ERRORS.REQUIRED],
        USE_VAT: [ERRORS.REQUIRED],
        AMOUNT: [ERRORS.REQUIRED],
        AMOUNT_WITH_VAT: [ERRORS.REQUIRED],
      };
    },
  },
  watch: {
    useVAT: {
      handler(val) {
        if (val === this.useVatTypes.WITH_VAT.label) {
          this.amount = 0;
        } else if (val === this.useVatTypes.WITHOUT_VAT.label) {
          this.amountWithVAT = 0;
        } else if (this.isNew && val === null) {
          this.useVAT = this.useVatTypes.WITH_VAT.label;
        }
      },
      immediate: true,
    },
    paymentPackageType: {
      handler(val) {
        if (this.isNew && val === null) {
          this.paymentPackageType = this.paymentPackageTypes.SUPPORT.value;
        }
      },
      immediate: true,
    },
  },
  created() {
    if (+this.id) {
      packagesApi.getPackage(this.id).then((pack) => {
        this.title = pack.title;
        this.article = pack.article;
        this.paymentPackageType = pack.paymentPackageType;
        this.amount = pack.amount;
        this.amountWithVAT = pack.amountWithVAT;
        this.comments = pack.comments;
        this.baseRateHours = pack.baseRateHours;
        this.useVAT = pack.useVAT;
      });
    }
  },
  methods: {
    async save() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      }

      this.loading = true;

      const paymentPackage = {
        title: this.title,
        article: this.article,
        useVAT: this.getUseVatType(),
        amount: this.amount,
        amountWithVAT: this.amountWithVAT,
        paymentPackageType: +this.paymentPackageType,
        comments: this.comments,
        baseRateHours: this.baseRateHours,
      };

      try {
        if (!this.isNew) {
          await packagesApi.updatePackage(+this.id, paymentPackage);
        } else {
          await packagesApi.createPackage(paymentPackage);
        }
        this.$notifySuccess();
        this.modal ? this.$emit("close") : this.$router.go(-1);
      } catch {
        this.$notifyWarning();
      } finally {
        this.loading = false;
        this.isChanged = false;
      }
    },
    getUseVatType() {
      let useVAT = "";
      for (const useVatType in this.useVatTypes) {
        if (this.useVAT === this.useVatTypes[useVatType].label) {
          useVAT = this.useVatTypes[useVatType].value;
        }
      }
      return useVAT;
    },
  },
};
</script>
